import MenuBookIcon from "@mui/icons-material/MenuBook";
import { PrimaryNavLink, Team as TWTeam } from "@teamworksdev/nav";
import { LDFlagSet } from "launchdarkly-react-client-sdk";
import { LinkProps, Link as ReactRouterLink } from "react-router-dom";
import { EnvironmentType } from "../../contexts/useEnvType";
import { trackEvent } from "../../reporting/reporting";
import { MyAxleDataForNavbarQuery } from "../../types";
import { amsDashboardLink } from "../../views/AMSDashboardMFE/Path";
import { devEnv } from "../NavUtils";
import { NAV_UPDATE_CHECK } from "./AuthPaths";
import { orgDevLink, staffDevLink } from "./Dev/DevRouter";
import { dataLink } from "./Org/Data/DataRouter";
import { educationResourcesLink } from "./Org/EducationResources/EducationResourcesPaths";
import { orgFoodManagementLink } from "./Org/FoodManagement/FoodManagementPaths";
import { kitchenLink } from "./Org/Kitchen/KitchenPaths";
import { mealPlansLink } from "./Org/MealPlans/MealPlansPaths";
import { messageLink } from "./Org/Message/MessagePaths";
import { unityMessageLink } from "./Org/Message/Unity/Path";
import { orgSettingsLink } from "./Org/Settings/SettingsPaths";
import { teamManagementLink } from "./Org/TeamManagement/TeamManagementPaths";
import { FeatureFlagsDetails, OrgUserDetails } from "./OrgUtils";
import { staffFoodManagementLink } from "./Staff/FoodManagement/FoodManagementRouter";
import { formsAndScoresLink } from "./Staff/FormsAndScores/FormsAndScoresRouter";
import { mealPlanManagementLink } from "./Staff/MealPlanManagement/MealPlanManagementRouter";
import { orgManagementLink } from "./Staff/OrgManagement/OrgManagementRouter";
import { restaurantManagementLink } from "./Staff/RestaurantManagement/RestaurantManagementRouter";
import { staffSettingsLink } from "./Staff/Settings/SettingsPaths";

export const STAFF_ORG_ID = "staff";
export const STAFF_ORG_NAME = "TW Nutrition Staff";
export const DEFAULT_ORG_ID = "UnknownOrgId";
export const DEFAULT_USER_NAME = "UnknownUser";

type NutritionModuleName =
  | "Team Management"
  | "Kitchen"
  | "Food Management"
  | "Meal Plans"
  | "Resources"
  | "AMS Dashboard"
  | "Messages"
  | "Data";

const withNutritionModuleTracking = (link: PrimaryNavLink, moduleName: NutritionModuleName): PrimaryNavLink => ({
  ...link,
  onClick: event => {
    link.onClick?.(event);
    trackEvent("nutrition_module_clicked", { name: moduleName });
  },
});

export const staffOrg: TWTeam = {
  id: STAFF_ORG_ID,
  name: STAFF_ORG_NAME,
};

export interface OrgAndStaffRouterProps {
  orgs: TWTeam[];
  onOrgChange: (orgId: string) => void;
  signedUserData?: string;
  axleDataForNavbar?: MyAxleDataForNavbarQuery["myAxleDataForNavbar"];
}

const updateCheckLink = {
  name: "Check for Updates",
  to: NAV_UPDATE_CHECK,
};

export const orgAdditionalSettingsLinks = [updateCheckLink, ...(devEnv ? [orgSettingsLink] : [])];
export const staffAdditionalSettingsLinks = [updateCheckLink, ...(devEnv ? [staffSettingsLink] : [])];

type AppAccess = {
  hasAMS: boolean;
  hasHub: boolean;
};

export const parseAxleAppAccess = (appAccess: ReadonlyArray<{ slug: string; targetUrl: string }> | null | undefined) => {
  const axleAppsForAppSwitcher = appAccess?.reduce((acc, { slug, targetUrl }) => {
    acc[slug] = targetUrl;
    return acc;
  }, {} as Record<string, string>);
  return {
    axleAppsForAppSwitcher,
    hasAMS: Boolean(axleAppsForAppSwitcher?.ams),
    hasHub: Boolean(axleAppsForAppSwitcher?.hub),
  };
};
export const getOrgPrimaryNavLinks = (
  { hasRoleDietitian, hasRoleChef, hasRoleFoodManager, hasRoleKiosk, hasRoleNonRdAdmin, axlePersonDocumentId }: OrgUserDetails,
  { hasPerformanceKitchen, hasMessaging, hasEducationResources }: FeatureFlagsDetails,
  { isUnityMessagingEnabled, isAmsDashboardEnabled }: LDFlagSet,
  { hasAMS, hasHub }: AppAccess,
  { isCommercial }: EnvironmentType
): PrimaryNavLink[] => {
  return [
    ...(hasAMS && isAmsDashboardEnabled ? [withNutritionModuleTracking(amsDashboardLink, "AMS Dashboard")] : []),
    ...(hasRoleDietitian || hasRoleNonRdAdmin ? [withNutritionModuleTracking(teamManagementLink, "Team Management")] : []),
    ...(hasPerformanceKitchen && (hasRoleDietitian || hasRoleChef || hasRoleKiosk || hasRoleNonRdAdmin)
      ? [withNutritionModuleTracking(kitchenLink, "Kitchen")]
      : []),
    ...(hasRoleFoodManager ? [withNutritionModuleTracking(orgFoodManagementLink, "Food Management")] : []),
    ...(hasRoleDietitian ? [withNutritionModuleTracking(mealPlansLink, "Meal Plans")] : []),
    ...(hasMessaging &&
    isCommercial &&
    !isUnityMessagingEnabled &&
    (hasRoleDietitian || hasRoleChef || hasRoleFoodManager || hasRoleNonRdAdmin)
      ? [withNutritionModuleTracking(messageLink, "Messages")]
      : []),
    ...(hasHub && isUnityMessagingEnabled ? [withNutritionModuleTracking(unityMessageLink, "Messages")] : []),
    ...(hasEducationResources && (hasRoleDietitian || hasRoleNonRdAdmin)
      ? [withNutritionModuleTracking(educationResourcesLink, "Resources")]
      : []),
    ...(hasRoleDietitian || hasRoleNonRdAdmin ? [withNutritionModuleTracking(dataLink, "Data")] : []),
    ...(devEnv ? [orgDevLink] : []),
  ];
};

export const getStaffPrimaryNavLinks = () => {
  return [
    orgManagementLink,
    staffFoodManagementLink,
    restaurantManagementLink,
    mealPlanManagementLink,
    formsAndScoresLink,
    ...(devEnv ? [staffDevLink] : []),
  ];
};

export const staffPrimaryNavTeam = {
  id: STAFF_ORG_ID,
  name: STAFF_ORG_NAME,
  //avatar: "https://images-teamworksapp.s3.amazonaws.com/164/organizationLogo/original/94453C44-C053-20D0-3B89AE8B1FB53613.png",
};

type BottomLinksHandlers = {
  userManagementHandler: () => void;
};
export const getOrgBottomLinks = ({ userManagementHandler }: BottomLinksHandlers) => [
  {
    name: "User Management",
    icon: <MenuBookIcon />,
    onClick: userManagementHandler,
    to: {} as string,
  },
];

export const helperMenuLinks = [
  {
    name: "Contact Support",
    onClick: () => {
      const newWindow = window.open("mailto:support@teamworks.com");
      trackEvent("Nutrition | Web | Teamworks Help | Contact Support", {});
      if (newWindow) {
        newWindow.opener = null;
      }
    },
    to: {} as string,
  },
  {
    name: "What's New",
    onClick: () => {
      const newWindow = window.open("https://explore.teamworks.com/performance-updates-january-2025#nutrition");
      trackEvent("Nutrition | Web | Teamworks Help | What’s New", {});
      if (newWindow) {
        newWindow.opener = null;
      }
    },
    to: {} as string,
  },
];

export const tacticalHelperMenuLinks = [
  {
    name: "Help Center",
    onClick: () => {
      const newWindow = window.open("https://help.teamworkstactical.com/nutrition/s/");
      if (newWindow) {
        newWindow.opener = null;
      }
    },
    to: {} as string,
  },
  {
    name: "Contact Support",
    onClick: () => {
      const newWindow = window.open("mailto:tacsupport@teamworks.com");
      if (newWindow) {
        newWindow.opener = null;
      }
    },
    to: {} as string,
  },
];

export const Link = ({ to, children, ...props }: LinkProps) => {
  if (typeof to === "string" && to.toString()?.startsWith("http")) {
    return (
      <a {...props} href={to?.toString()}>
        {children}
      </a>
    );
  }
  return (
    <ReactRouterLink {...props} to={to}>
      {children}
    </ReactRouterLink>
  );
};
